import { computed, effect, signal } from "@preact/signals";
import * as CryptoJS from "CryptoJS_MD5";
import * as _ from "underscore";

import * as featureFlags from "featureFlags";
import * as impersonation from "impersonation";
import readlangClient from "readlangClient";
import { hasFeature } from "shared/featureFlagKeys";
import * as languageMap from "shared/languageMap";
import { defaultTranslationStyle } from "shared/readerSettings";
import * as tts from "tts";

export type UserID = string;
export type FeatureFlagMap = Partial<Record<featureFlags.UserFlagKey, boolean>>;
export type LanguagePreferenceMap = Record<string, boolean>;
/** Map of learning language code to another language code (e.g. its preferred
 *  first/translation language). */
export type LanguageCodeMap = Record<string, string>;
export type ReadingSpeeds = Record<string, number>;
export type CustomDictionaries = Record<string, string | boolean>;
export type GooglePlusInfo = {
  image?: { url?: string };
  picture?: string;
};

export interface PublicBooksFilter {
  includeAI: boolean;
  metrics_CEF: string;
  metrics_words: string;
  /** Show only texts the user has liked (hearted). */
  likedOnly?: boolean;
  /** Show only texts with a video (a YouTube import). */
  videoOnly?: boolean;
  /** Include texts the user has completed. Defaults to true. */
  includeCompleted?: boolean;
}

/** Someone whose public texts the user has hidden by blocking them. */
export interface BlockedUser {
  _id: UserID;
  username: string;
}

export interface ExportSettings {
  delimiter?: string;
  excludeExported?: boolean;
  fields?: string[];
}

export interface User {
  _id: UserID;

  passwordHash: string;
  passwordSalt: string;
  passwordIterations: number;
  passwordAlgorithm: string;

  email: string;
  username: string; // lowercase, URL-safe identifier
  displayName: string; // user-facing display name
  firstLanguage: string;
  currentLearningLanguage: string;
  /** Preferred first (translation) language per learning language code. */
  preferredFirstLanguages?: LanguageCodeMap;

  sortBooksBy: string; // deprecated
  publicSortBooksBy: string;
  privateSortBooksBy: string;

  publicBooksFilter: PublicBooksFilter;
  publicBooksCategories: string[];

  resetPasswordCode: string;
  resetPasswordDate: Date;

  publicByDefault: boolean;

  acheivements: string[]; // TODO: refactor - fix typo
  activeLanguages: string[]; // ones with books

  subscriptionPlan: string; // subscribed / free / freeForever / expired
  subscriptionExpiryDate: Date;
  billingAddressNeeded: boolean;

  location: string;
  homepageURL: string;
  about: string;

  customDictionaries: CustomDictionaries;

  typingMode: boolean;
  speakingMode: boolean;
  passiveModeLanguages: string[];
  activeModeLanguages: string[];

  emailNotifications: string[];
  emailStatus: string; // unknown, invalid, validated, unsubscribed
  unsubscribeCode: string;
  /** Whether the account has a password, which an email change requires. */
  hasPassword?: boolean;
  /** A requested new address still waiting to be confirmed from its inbox. */
  pendingEmail?: string;

  lifecycleEmailsSent: string[]; // initialHelp

  teacher: boolean; // Deprecated

  referrer: string;
  phraseLimit: number;

  googlePlusInfo: GooglePlusInfo;

  readerFont: string;
  readerFontCJK: string;
  readerAlign: string;
  readerSentenceBreaks: string;
  readerTranslationStyle: string;
  readerWordHighlights: string;
  readerMergePhrases: string;
  readerTranslationFont: string;
  readerTranslations: string;
  readerExplanations: string;
  readerMonolingualAutoOpen: string;
  readerStreakShow: string;
  readerDeselectBehavior: string;
  readerReview: string;

  bookmarkletPosition: string;
  bookmarkletCollapsed: boolean;
  bookmarkletColor: string;

  timezoneOffset: number; // minutes relative to UTC, from browser: dateUtils.getTimezoneOffset()

  dailyPracticeTarget: number;

  exportSettings: ExportSettings;

  smartDictionary: boolean;
  smartDictionaryInEnglish: LanguagePreferenceMap;
  reviewSummaryInEnglish: LanguagePreferenceMap;

  readingSpeeds: ReadingSpeeds;

  emailMD5: string;

  practiceType: string; // new, blitz, classic
  practiceLength: string; // verySmall, small, medium, large, veryLarge
  contextDifficulty: string; // beginner, intermediate, advanced
  reviewPace?: "closer" | "standard" | "wider" | "widest"; // SRS review spacing preset

  updateHour: number; // This doesn't actually seem to be used, it's always 0

  forumAccess?: "granted" | "denied";

  featureFlags: FeatureFlagMap;

  soundEffectsEnabled?: boolean;

  /** Per-feature server-synced TTS voice picks, scoped per language. */
  ttsVoices?: {
    [language: string]: {
      readAloud?: { provider: string; id: string; rate?: number };
      speakingMode?: { provider: string; id: string; rate?: number };
      provider?: string;
      id?: string;
      rate?: number;
    };
  };

  hideWalkthrough?: boolean;
  // When the user agreed to their text going to AI services (aiConsent.ts)
  aiConsentedAt?: string;

  blockedUsers?: BlockedUser[];
}

export type UserAttrs = Partial<User>;
export type ReaderSettings = Partial<
  Pick<
    User,
    | "readerAlign"
    | "readerDeselectBehavior"
    | "readerFont"
    | "readerFontCJK"
    | "readerMergePhrases"
    | "readerMonolingualAutoOpen"
    | "readerReview"
    | "readerSentenceBreaks"
    | "readerStreakShow"
    | "readerTranslationFont"
    | "readerTranslationStyle"
    | "readerTranslations"
    | "readerWordHighlights"
  >
>;

const buildDefaults = (): UserAttrs => ({
  username: "guest",
  displayName: "Guest",
  currentLearningLanguage: "es",
  firstLanguage: "en",
  updateHour: 0,
  sortBooksBy: "readDate",
  publicBooksCategories: ["All"],
  publicBooksFilter: {
    includeAI: true,
    metrics_CEF: "A1-C2",
    metrics_words: "0-Infinity",
  },
  publicSortBooksBy: "hotness",
  dailyPracticeTarget: 10,
  customDictionaries: {},

  readerAlign: "alignLeft",
  readerDeselectBehavior: "delete",
  readerWordHighlights: "off",
  readerFont: "Jost",
  readerFontCJK: "Noto Sans",
  readerTranslations: "on",
  readerExplanations: "off",
  readerMonolingualAutoOpen: "on",
  readerSentenceBreaks: "off",
  readerTranslationStyle: defaultTranslationStyle,
  readerMergePhrases: "on",
  readerTranslationFont: "cursive",
  readerStreakShow: "always",

  practiceLength: "small",
  contextDifficulty: "intermediate",
  smartDictionaryInEnglish: {},
  reviewSummaryInEnglish: {},
  soundEffectsEnabled: true,
  speakingMode: false,
  featureFlags: {},
});

export default class UserStore {
  readonly state = signal<UserAttrs>(buildDefaults());

  readonly currentLearningLanguage = computed(
    () => this.state.value.currentLearningLanguage
  );
  readonly firstLanguage = computed(() => this.state.value.firstLanguage);
  readonly speakingMode = computed(() =>
    Boolean(this.state.value.speakingMode)
  );
  readonly readerTranslations = computed(
    () => this.state.value.readerTranslations
  );
  readonly featureFlags = computed(() => this.state.value.featureFlags ?? {});
  readonly emailNotifications = computed(
    () => this.state.value.emailNotifications ?? []
  );
  readonly publicBooksCategories = computed(
    () => this.state.value.publicBooksCategories ?? []
  );
  readonly publicBooksFilter = computed(
    () => this.state.value.publicBooksFilter
  );
  readonly blockedUsers = computed(() => this.state.value.blockedUsers ?? []);
  readonly id = computed(() => this.state.value._id as string | undefined);
  readonly isNew = computed(() => !this.id.value);
  readonly isGod = computed(() => this.id.value === "50cb58dbf939c7397b000002");
  readonly isLocalMode = computed(() => this.id.value === "local");

  readonly emailMD5 = computed(() => {
    const email = this.state.value.email;
    return email ? CryptoJS.MD5(email).toString() : undefined;
  });

  readonly hasPremiumAccess = computed(() =>
    ["standard", "plus", "freeForever"].includes(
      this.state.value.subscriptionPlan!
    )
  );

  readonly hasPremiumPlusAccess = computed(() =>
    ["plus", "freeForever"].includes(this.state.value.subscriptionPlan!)
  );

  readonly flashcardMode = computed<"passive" | "active" | "mixed">(() => {
    const lang = this.currentLearningLanguage.value;
    if (lang && this.state.value.passiveModeLanguages?.includes(lang)) {
      return "passive";
    }
    if (lang && this.state.value.activeModeLanguages?.includes(lang)) {
      return "active";
    }
    return "mixed";
  });

  readonly hasChatFeature = computed(
    () =>
      this.hasPremiumAccess.value &&
      languageMap.languageSupportsChat(this.currentLearningLanguage.value!)
  );

  readonly readerSettings = computed<Partial<ReaderSettings>>(() => {
    const {
      readerAlign,
      readerDeselectBehavior,
      readerFont,
      readerFontCJK,
      readerMergePhrases,
      readerMonolingualAutoOpen,
      readerReview,
      readerSentenceBreaks,
      readerStreakShow,
      readerTranslationFont,
      readerTranslationStyle,
      readerTranslations,
      readerWordHighlights,
    } = this.state.value;
    return {
      readerAlign,
      readerDeselectBehavior,
      readerFont,
      readerFontCJK,
      readerMergePhrases,
      readerMonolingualAutoOpen,
      readerReview,
      readerSentenceBreaks,
      readerStreakShow,
      readerTranslationFont,
      readerTranslationStyle,
      readerTranslations,
      readerWordHighlights,
    };
  });

  readonly imageURL = computed<string>(() => {
    const a = this.state.value;
    if (a.googlePlusInfo?.image?.url) {
      return a.googlePlusInfo.image.url;
    }
    if (a.googlePlusInfo?.picture) {
      return a.googlePlusInfo.picture;
    }
    return `//gravatar.com/avatar/${this.emailMD5.value ?? ""}?d=mm&s=72`;
  });

  readonly viewJSON = computed(() => {
    const a = this.state.value;
    const firstLanguageInfo = a.firstLanguage
      ? languageMap.map[a.firstLanguage]
      : undefined;
    const currentLearningLanguageInfo = a.currentLearningLanguage
      ? languageMap.map[a.currentLearningLanguage]
      : undefined;
    return {
      ...a,
      emailMD5: this.emailMD5.value,
      imageURL: this.imageURL.value,
      firstLanguageString: firstLanguageInfo?.name,
      firstLanguageEnglishString: firstLanguageInfo?.english,
      currentLearningLanguageString: currentLearningLanguageInfo?.name,
      currentLearningLanguageEnglishString:
        currentLearningLanguageInfo?.english,
      godMode: this.isGod.value || undefined,
    };
  });

  constructor(initial?: UserAttrs) {
    if (initial) {
      this.state.value = { ...this.state.value, ...initial };
    }
  }

  /**
   * Runs `callback` after the selected user value changes.
   *
   * Use this for imperative side effects and non-signal-aware code. Preact
   * components should usually read signals directly during render.
   */
  onChange<T>(
    selector: (attrs: UserAttrs) => T,
    callback: (current: T, previous: T) => void
  ): () => void {
    let previous = selector(this.state.value);
    let first = true;
    return effect(() => {
      const current = selector(this.state.value);
      if (first) {
        first = false;
        previous = current;
        return;
      }
      if (!_.isEqual(current, previous)) {
        const old = previous;
        previous = current;
        callback(current, old);
      }
    });
  }

  /** Immutably merges `updates` into `attrs`. No server round-trip. */
  patch(updates: UserAttrs): void {
    this.state.value = { ...this.state.value, ...updates };
  }

  /**
   * Mirrors the server's `updateActiveLanguages`: unshifts `language` to the
   * front of `activeLanguages`, dedupes, and caps at 10. Use this after a
   * client action that causes the server to do the same, so the local store
   * doesn't drift while the `userFetchThrottle` window is still active.
   */
  markLanguageActive(language: string): void {
    const current = this.state.value.activeLanguages ?? [];
    const next = [
      language,
      ...current.filter((lang) => lang !== language),
    ].slice(0, 10);
    if (_.isEqual(current, next)) return;
    this.patch({ activeLanguages: next });
  }

  /** Optimistic save: applies updates locally, then PATCHes the server. */
  async save(updates: UserAttrs): Promise<void> {
    this.patch(updates);
    await readlangClient.request<unknown>("PATCH", "/api/user", updates);
  }

  /**
   * Switches the first (translation) language and remembers it as the preferred
   * pairing for the current learning language, so switching back to that
   * learning language later auto-restores it. See `selectLearningLanguage`.
   */
  async selectFirstLanguage(firstLanguage: string): Promise<void> {
    const learningLanguage = this.state.value.currentLearningLanguage;
    const updates: UserAttrs = { firstLanguage };
    if (learningLanguage && firstLanguage !== learningLanguage) {
      updates.preferredFirstLanguages = {
        ...(this.state.value.preferredFirstLanguages ?? {}),
        [learningLanguage]: firstLanguage,
      };
    }
    await this.save(updates);
  }

  /**
   * Switches the learning language, auto-restoring the first (translation)
   * language previously paired with it. See `selectFirstLanguage`.
   */
  async selectLearningLanguage(currentLearningLanguage: string): Promise<void> {
    const updates: UserAttrs = { currentLearningLanguage };
    const preferred =
      this.state.value.preferredFirstLanguages?.[currentLearningLanguage];
    if (preferred && preferred !== this.state.value.firstLanguage) {
      updates.firstLanguage = preferred;
    }
    await this.save(updates);
  }

  /** Fetches the full user record from the server and merges it into attrs. */
  async fetch(): Promise<void> {
    const { data } = await readlangClient.request<
      UserAttrs & { impersonation?: impersonation.ImpersonationInfo }
    >("GET", "/api/user", {});
    if (data) {
      // Not a user attribute — it describes the session, so it is kept out of
      // attrs and published to the banner instead.
      const { impersonation: impersonationInfo, ...userAttrs } = data;
      impersonation.info.value = impersonationInfo;
      this.patch(
        // Users not yet migrated to the username/displayName split have no
        // displayName — fall back to their username rather than the "Guest"
        // default.
        userAttrs.username && !userAttrs.displayName
          ? { ...userAttrs, displayName: userAttrs.username }
          : userAttrs
      );
    }
  }

  isBlocked(userID: string): boolean {
    return this.blockedUsers.value.some((user) => user._id === userID);
  }

  /** Blocking a user hides all their public texts from this user. */
  async setBlocked(userID: string, blocked: boolean): Promise<void> {
    const { data } = await readlangClient.request<{
      blockedUsers: BlockedUser[];
    }>(blocked ? "POST" : "DELETE", `/api/user/blockedUsers/${userID}`, {});
    this.patch(data);
  }

  setFlashcardMode(mode: "passive" | "active" | "mixed"): Promise<void> {
    const lang = this.currentLearningLanguage.value;
    if (!lang) return Promise.resolve();
    const passive = new Set(this.state.value.passiveModeLanguages ?? []);
    const active = new Set(this.state.value.activeModeLanguages ?? []);
    passive.delete(lang);
    active.delete(lang);
    if (mode === "passive") passive.add(lang);
    if (mode === "active") active.add(lang);
    return this.save({
      passiveModeLanguages: [...passive],
      activeModeLanguages: [...active],
    });
  }

  /**
   * Whether the smart dictionary should display in the user's first language
   * for the given learning language. Defaults to true if unset.
   */
  isSmartDictionaryInFirstLanguage(language: string): boolean {
    const map = this.state.value.smartDictionaryInEnglish ?? {};
    return map[language] ?? true;
  }

  setSmartDictionaryInFirstLanguage(
    language: string,
    inEnglish: boolean
  ): Promise<void> {
    return this.save({
      smartDictionaryInEnglish: {
        ...(this.state.value.smartDictionaryInEnglish ?? {}),
        [language]: inEnglish,
      },
    });
  }

  isReviewSummaryInEnglish(language: string): boolean {
    const map = this.state.value.reviewSummaryInEnglish ?? {};
    return map[language] ?? true;
  }

  setReviewSummaryToEnglish(
    language: string,
    inEnglish: boolean
  ): Promise<void> {
    return this.save({
      reviewSummaryInEnglish: {
        ...(this.state.value.reviewSummaryInEnglish ?? {}),
        [language]: inEnglish,
      },
    });
  }

  getReadingSpeed(language: string): number {
    const defaultSpeed = 180;
    const speeds = this.state.value.readingSpeeds ?? {};
    return Math.round(speeds[language] ?? defaultSpeed);
  }

  /**
   * Updates the user's reading speed using a dampened exponential moving
   * average. Persists to the server.
   */
  updateReadingSpeed(language: string, newReadingSpeed: number): Promise<void> {
    const dampingFactor = 0.8;
    const maxChange = 15;
    const current = this.getReadingSpeed(language);

    const delta = Math.max(
      -maxChange,
      Math.min(maxChange, (newReadingSpeed - current) * (1 - dampingFactor))
    );

    const speeds = this.state.value.readingSpeeds ?? {};
    return this.save({
      readingSpeeds: { ...speeds, [language]: current + delta },
    });
  }

  async hasNewStreakFeature(): Promise<boolean> {
    return !(await readlangClient.isLocalMode());
  }

  hasFeature(featureKey: featureFlags.UserFlagKey): boolean {
    // Shared with the server, which applies the same predicate before letting a
    // flag select a paid model
    return hasFeature(
      {
        subscriptionPlan: this.state.value.subscriptionPlan,
        featureFlags: this.featureFlags.value,
      },
      featureKey
    );
  }

  setFeature(
    featureKey: featureFlags.UserFlagKey,
    value: boolean
  ): Promise<void> {
    return this.save({
      featureFlags: {
        ...(this.state.value.featureFlags ?? {}),
        [featureKey]: value,
      },
    });
  }

  /**
   * The Auto-Pronounce playback gate.
   *
   * Treat "checking" as enabled so iOS Safari can still start playback before
   * the browser voice cache is populated. `tts.play` awaits voice loading and
   * exits cleanly if no provider can speak the language.
   */
  speakingModeEnabled(language: string): boolean {
    if (!this.speakingMode.value) {
      return false;
    }
    return (
      tts.getAvailabilitySignal({
        user: this,
        feature: "speakingMode",
        language,
      }).value !== "unsupported"
    );
  }
}
